import React from "react";
import { TransitionGroup } from "react-transition-group";
import {
  Box,
  Button,
  Typography,
  Collapse,
  Stack,
  SxProps,
  useTheme,
} from "@mui/material";
import CompareArrowsSharp from "@mui/icons-material/CompareArrowsSharp";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { useSelector } from "react-redux";
import {
  changeFlights,
  searchFormSelector,
  IFlight,
  IFlightDates,
} from "redux/searchForm";
import { UseAppDispatch } from "redux/store";
import AirportSearch, { ILocation } from "./AirportSearch/AirportSearch";
import AddFlightBtn from "../AddFlight/AddFlight";
import TravelDates from "../TravelDates/TravelDates";

import { initialFlightObj } from "redux/filters/initialState";
import { useTranslations } from "hooks/useTranslations";
import { dateObjToDateStringDotted } from "@thekilroygroup/shared-ui";

export default function FromTo() {
  const { flights, tripType } = useSelector(searchFormSelector);
  const dispatch = UseAppDispatch();
  const {
    transitions: { easing, duration },
  } = useTheme();

  const {
    translations: {
      search: {
        fromTo: {
          addFlights,
          multiHeader,
          arrival,
          departure,
          noOptionsText,
          deleteText,
        },
      },
    },
  } = useTranslations();

  const handleBlur = (flights: IFlight[]) => {
    dispatch(changeFlights(flights));
  };

  /**
   * Change airport
   */
  const handleChangeFlight = (
    option: ILocation | string | null,
    index: number,
    fromOrTo: "departure" | "arrival",
  ) => {
    const iata =
      option !== null && typeof option !== "string" && option.code !== null
        ? option.code
        : null;

    const nextFlight = {
      ...flights[index],
      [fromOrTo]: iata,
    };
    let nextFlights = [...flights];
    nextFlights[index] = nextFlight;

    if (tripType === "RETURN") {
      const nextReturnFlight = {
        ...flights[1],
        departure: nextFlights[0].arrival,
        arrival: nextFlights[0].departure,
      };
      nextFlights[1] = nextReturnFlight;
    }
    dispatch(changeFlights(nextFlights));
  };

  /**
   * Flip from and to airports
   */
  const flipFlights = (flights: IFlight[], index: number) => {
    let nextFlights = [...flights];

    const nextDepartureFlight: IFlight = {
      ...initialFlightObj,
      departure: nextFlights[index].arrival,
      arrival: nextFlights[index].departure,
      departureDate: nextFlights[index].departureDate,
    };

    nextFlights[index] = nextDepartureFlight;

    if (tripType === "RETURN") {
      const nextDepartureFlight: IFlight = {
        ...initialFlightObj,
        departure: nextFlights[1].arrival,
        arrival: nextFlights[1].departure,
        departureDate: nextFlights[1].departureDate,
      };

      nextFlights[1] = nextDepartureFlight;
    }

    dispatch(changeFlights(nextFlights));
  };

  /**
   * Add a new flight
   */
  const addFlight = () => {
    let nextFlights = [...flights];
    const nextFromTo = initialFlightObj;
    nextFlights.push(nextFromTo);
    dispatch(changeFlights(nextFlights));
  };

  /**
   * Delete a flight
   */
  const deleteFlight = (index: number) => {
    let nextFlights = [...flights];
    nextFlights.splice(index, 1);
    dispatch(changeFlights(nextFlights));
  };

  /**
   * Change dates of a flight
   */
  const changeDates = (nextDates: IFlightDates, index: number) => {
    let nextFlights = [...flights];
    nextFlights[index] = {
      ...nextFlights[index],
      departureDate: dateObjToDateStringDotted(nextDates.departureDate),
    };
    if (tripType === "RETURN") {
      nextFlights[1] = {
        ...nextFlights[1],
        departureDate:
          nextDates.returnDate !== null
            ? dateObjToDateStringDotted(nextDates.returnDate)
            : null,
      };
    }
    dispatch(changeFlights(nextFlights));
  };

  const {
    wrapper,
    box,
    isMultiHeader,
    iconButton,
    datesWrapper,
    deleteBtn,
    deleteBtnParagraph,
    deleteBtnIcon,
  } = sxStyles;

  return (
    <Box sx={wrapper}>
      {/* @ts-ignore */}
      <TransitionGroup>
        {flights.map((flight, index) => {
          return (
            <Collapse
              key={`transition-${index}`}
              in={true}
              easing={easing.easeIn}
              timeout={duration.shortest}
            >
              <Box key={`box-${index}`} sx={box}>
                <Collapse
                  in={tripType === "MULTI_DEST"}
                  easing={easing.easeIn}
                  timeout={duration.shortest}
                >
                  <Typography variant="h6" sx={isMultiHeader}>
                    {index + 1}. {multiHeader}
                  </Typography>
                </Collapse>

                {!(tripType === "RETURN" && index === 1) && (
                  <Stack
                    sx={{
                      flexDirection: { md: "row" },
                    }}
                  >
                    <Stack
                      sx={{
                        flexDirection: { md: "row" },
                        width: 1,
                        position: "relative",
                      }}
                    >
                      <AirportSearch
                        fromOrTo="departure"
                        label={departure.label}
                        noOptionsText={noOptionsText}
                        onChange={handleChangeFlight}
                        onBlur={handleBlur}
                        index={index}
                        flights={flights}
                        iata={flight.departure}
                      />
                      <Button
                        sx={iconButton}
                        color="primary"
                        variant="contained"
                        disableElevation={true}
                        aria-label="Flip departure and arrival airports"
                        onClick={() => flipFlights(flights, index)}
                      >
                        <CompareArrowsSharp fontSize="medium" />
                      </Button>
                      <AirportSearch
                        fromOrTo="arrival"
                        label={arrival.label}
                        noOptionsText={noOptionsText}
                        onChange={handleChangeFlight}
                        onBlur={handleBlur}
                        index={index}
                        flights={flights}
                        iata={flight.arrival}
                      />
                    </Stack>
                    <Box
                      sx={{
                        ...datesWrapper,
                        width: {
                          xs: 1,
                          md: `${tripType === "RETURN" ? 0.5 : 0.4}`,
                        },
                        transitionDuration: `${duration.standard}ms`,
                        transitionTimingFunction: easing.easeOut,
                      }}
                    >
                      <TravelDates
                        index={index}
                        flights={flights}
                        onDateChange={changeDates}
                        onBlur={handleBlur}
                      />
                    </Box>
                  </Stack>
                )}
                {tripType === "MULTI_DEST" && index > 0 && (
                  <Button
                    variant="text"
                    sx={deleteBtn}
                    onClick={() => deleteFlight(index)}
                  >
                    <Typography variant="bodyMedium" sx={deleteBtnParagraph}>
                      {deleteText}
                    </Typography>
                    <DeleteOutline color="primary" sx={deleteBtnIcon} />
                  </Button>
                )}
              </Box>
            </Collapse>
          );
        })}
        {tripType === "MULTI_DEST" && (
          <Collapse
            in={tripType === "MULTI_DEST"}
            easing={easing.easeIn}
            timeout={duration.shortest}
          >
            <AddFlightBtn title={addFlights} onClick={addFlight} />
          </Collapse>
        )}
      </TransitionGroup>
    </Box>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  wrapper: {
    position: "relative",
    width: { md: "300%" },
    flexDirection: { md: "row" },
    mb: { md: 0 },
    mr: { md: 1 },
  },
  datesWrapper: {
    ml: { md: 1 },
    transitionProperty: "width",
  },
  box: {
    position: "relative",
  },
  iconButton: {
    fontSize: 8 * 3.6,
    p: 0.5,
    zIndex: 1,
    minWidth: 0,
    minHeight: 0,
    borderRadius: 0,
    position: "absolute",
    right: { xs: 8, md: "initial" },
    left: { md: "50%" },
    top: { xs: "50%", md: 20 },
    transform: {
      xs: "translate(0, -50%) rotate(90deg)",
      md: "translate(-50%, 0) rotate(0deg)",
    },
  },
  deleteBtn: {
    color: "grey.500",
    position: "absolute",
    top: { xs: "0", md: 6.25 * 8 },
    right: { xs: "0", md: -16 },
    p: 0,
    textTransform: "initial",
    fontSize: "14px",
    mx: 0,
    transform: { md: "translate(100%,0)" },
  },
  deleteBtnParagraph: { fontSize: "14px" },
  deleteBtnIcon: { fontSize: "17px" },
  isMultiHeader: {
    mx: 1,
    my: 0,
    lineHeight: 1.6,
  },
};
